/* eslint-disable lingui/no-unlocalized-strings, lingui/text-restrictions -- Practice notebook copy; localised together with the rest of the practice area. */
import "./notebook.css";

import {
  PRACTICE_READINESS,
  PRACTICE_READINESS_LABELS,
  PRACTICE_RUBRIC,
  PRACTICE_RUBRIC_LEVELS,
  type PracticeReadiness,
  type PracticeRubricDimension,
  type PracticeRubricScore,
} from "@reactive-resume/schema";
import { Link2 } from "lucide-react";
import { type CSSProperties, type ReactNode, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

export const PRACTICE_HOME = "/dashboard/practice-interview";

export const prefersReducedMotion = () => {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
};

/**
 * A number that counts up once when it appears (ease-out-expo); static with reduced motion.
 * `from` counts up from a previous value rather than zero, and `delay` waits before starting,
 * so a count can follow the element's own entrance instead of racing it.
 */
export const CountUp = ({
  value,
  ms = 650,
  from = 0,
  delay = 0,
}: {
  value: number;
  ms?: number;
  from?: number;
  delay?: number;
}) => {
  const [shown, setShown] = useState(() => (prefersReducedMotion() ? value : from));
  useEffect(() => {
    if (prefersReducedMotion()) {
      setShown(value);
      return;
    }
    let frame = 0;
    const start = performance.now() + delay;
    const tick = (now: number) => {
      const t = Math.min(1, Math.max(0, (now - start) / ms));
      setShown(Math.round(from + (value - from) * (t === 1 ? 1 : 1 - 2 ** (-10 * t))));
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [value, ms, from, delay]);
  return <span>{shown}</span>;
};
export const PRACTICE_HISTORY = "/dashboard/practice-interview/history";

/** The notebook's page: folio ground, with the Practice / History tabs. */
export const NotebookPage = ({
  children,
  tabs = true,
  aside,
}: {
  children: ReactNode;
  tabs?: boolean;
  aside?: ReactNode;
}) => {
  const { pathname } = useLocation();
  return (
    <div className="pn">
      <div className="pn-page">
        {tabs && (
          <nav className="pn-nav" aria-label="Practice Interview">
            <div className="pn-tabs">
              <Link
                to={PRACTICE_HOME}
                aria-current={pathname === PRACTICE_HOME ? "page" : undefined}
              >
                Practice
              </Link>
              <Link
                to={PRACTICE_HISTORY}
                aria-current={pathname.startsWith(PRACTICE_HISTORY) ? "page" : undefined}
              >
                History
              </Link>
            </div>
            {aside}
          </nav>
        )}
        {children}
      </div>
    </div>
  );
};

export const ReadinessPill = ({ readiness }: { readiness: PracticeReadiness | null }) => {
  if (!readiness) return <span className="pn-pill pn-pill-keep">Not scored</span>;
  const tone =
    readiness === "interview_ready"
      ? "pn-pill-ready"
      : readiness === "almost_there"
        ? "pn-pill-almost"
        : "pn-pill-keep";
  return <span className={`pn-pill ${tone}`}>{PRACTICE_READINESS_LABELS[readiness]}</span>;
};

export const ReadinessTrack = ({ readiness }: { readiness: PracticeReadiness }) => (
  <div
    className="pn-ready-track"
    role="img"
    aria-label={`Readiness: ${PRACTICE_READINESS_LABELS[readiness]}`}
  >
    {PRACTICE_READINESS.map((level) => (
      <span key={level} data-on={level === readiness ? "" : undefined}>
        {PRACTICE_READINESS_LABELS[level]}
      </span>
    ))}
  </div>
);

const LevelDots = ({ level }: { level: number }) => (
  <span className="pn-dots" aria-hidden>
    {[0, 1, 2, 3].map((i) => (
      <i key={i} data-on={i <= level ? "" : undefined} style={{ "--d": i } as CSSProperties} />
    ))}
  </span>
);

/** One answer's rubric in words; numbers only when asked for. */
export const RubricRow = ({
  rubric,
  showScores,
  focus = null,
}: {
  rubric: PracticeRubricScore[];
  showScores: boolean;
  /** The part in focus on the rubric chart: drawn forward, the rest step back. */
  focus?: PracticeRubricDimension | null;
}) => (
  <span className="pn-rub" data-focusing={focus ? "" : undefined}>
    {rubric.map((r) => (
      <span
        key={r.dimension}
        className="pn-r"
        data-low={r.level <= 1 ? "" : undefined}
        data-focus={focus === r.dimension ? "" : undefined}
      >
        {PRACTICE_RUBRIC[r.dimension].label} <b>{PRACTICE_RUBRIC_LEVELS[r.level]}</b>
        <LevelDots level={r.level} />
        {showScores && <span className="pn-tnum">{r.level + 1}/4</span>}
        {r.closeCall && (
          <span
            className="pn-close-call"
            title="Our scorer was split between two levels here. Treat it as a close call."
          >
            close call
          </span>
        )}
      </span>
    ))}
  </span>
);

export const Cite = ({ url, source }: { url: string; source: string }) => (
  <a className="pn-cite" href={url} target="_blank" rel="noreferrer">
    <Link2 aria-hidden />
    {source}
  </a>
);

/** A small trend line: area, line and an emphasised latest point, scaled to its own range. */
export const Spark = ({ values }: { values: number[] }) => {
  if (values.length < 2) return null;
  const W = 200;
  const H = 52;
  const P = 5;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const x = (i: number) => P + (i * (W - 2 * P)) / (values.length - 1);
  const y = (v: number) => H - P - ((v - min) / (max - min || 1)) * (H - 2 * P);
  const points = values.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`);
  const area = `M${x(0)},${H - P} L${points.join(" L")} L${x(values.length - 1)},${H - P} Z`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden>
      <line x1={P} x2={W - P} y1={H - P} y2={H - P} stroke="var(--pn-rule)" strokeWidth={1} />
      <path d={area} fill="var(--pn-blue-wash)" />
      <polyline
        points={points.join(" ")}
        fill="none"
        stroke="var(--pn-blue)"
        strokeWidth={2}
        strokeLinejoin="round"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
        // Normalised so the line can draw itself with a stroke-dasharray of 1.
        pathLength={1}
      />
      <circle
        cx={x(values.length - 1)}
        cy={y(values[values.length - 1])}
        r={3.5}
        fill="var(--pn-blue)"
        stroke="var(--pn-paper)"
        strokeWidth={1.5}
      />
    </svg>
  );
};

export const Skeleton = ({ lines = 3 }: { lines?: number }) => (
  <div className="pn-skel" aria-hidden>
    {Array.from({ length: lines }, (_, i) => (
      <i key={i} style={{ width: `${[96, 88, 72, 92, 64][i % 5]}%` }} />
    ))}
  </div>
);

export const formatDay = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });
